import { IoClose } from "react-icons/io5";
import { useCallback, useEffect, useRef, useState } from "react";

import useOutsideClick from "../../../hooks/useOutsideClick";

/**
 * Open/close helpers for FormModalDialog.
 *
 * @returns {[boolean, Function, Function]} `[isOpen, handleOpen, handleClose]`.
 */
export function useModalDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);

  return [isOpen, handleOpen, handleClose];
}

/**
 * DEPRECIATED if replaced. Still used only for custom start time selection
 * on scopes (EditScopeCustomTimeModal).
 * Do not use for new UI; replace with current modal patterns when those screens are cleaned up.
 *
 * Generic form modal dialog that allows customizing the title, description and the
 * actual form to be submitted.
 *
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Function} props.onSubmit
 * @param {string} props.title
 * @param {*} props.descriptionSlot
 * @param {*} props.formSlot
 * @param {*} props.footerSlot
 * @returns {JSX.Element}
 */
export default function FormModalDialog({
  open,
  onClose,
  onSubmit,
  title,
  descriptionSlot,
  formSlot,
  footerSlot,
}) {
  const dialog = useRef(null);

  useEffect(() => {
    if (dialog.current) {
      if (open) {
        dialog.current.showModal();
      } else {
        dialog.current.close();
      }
    }
  }, [open, onClose]);

  // Outside click should close menu
  const modalRef = useRef(null);
  useOutsideClick(modalRef, () => onClose());

  return (
    <dialog onClose={onClose} autoFocus={true} ref={dialog}>
      <div className={"relative"}>
        <div
          className={"fixed top-0 left-0 w-full h-full bg-transparent"}
          onClick={onClose}
        ></div>
      </div>

      <section
        className={
          "flex flex-col space-y-4 text-gray-800 text-base rounded bg-white p-4 drop-shadow-lg"
        }
        ref={modalRef}
      >
        <header className={"flex flex-row justify-between"}>
          <h1 className={"font-semibold"}>{title}</h1>
          <button onClick={onClose} className={"text-2xl text-gray-500"}>
            <IoClose />
          </button>
        </header>
        <div className={"text-gray-500 text-sm"}>{descriptionSlot}</div>
        <form
          className={"flex flex-col space-y-4 text-sm caret-black"}
          onSubmit={onSubmit}
        >
          {formSlot}
          <footer>
            {footerSlot || (
              <div className={"flex flex-row"}>
                <button
                  type={"submit"}
                  className={
                    "grow text-white font-bold rounded bg-green-600 disabled:bg-gray-200 disabled:text-gray-400 active:bg-green-700 p-2"
                  }
                >
                  Submit
                </button>
              </div>
            )}
          </footer>
        </form>
      </section>
    </dialog>
  );
}
